import type { ReactNode } from 'react'

interface SimpleModalProps {
  eyebrow: string
  title?: string
  body: ReactNode
  actions: ReactNode
  topLayer?: boolean
  onBackdropClick?: () => void
}

function SimpleModal({
  eyebrow,
  title,
  body,
  actions,
  topLayer = false,
  onBackdropClick
}: SimpleModalProps): JSX.Element {
  return (
    <div
      className={topLayer ? 'modal-backdrop modal-backdrop-top' : 'modal-backdrop'}
      role="presentation"
      onClick={onBackdropClick}
    >
      <section
        className="modal-card"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="panel-heading">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            {title ? <h2>{title}</h2> : null}
          </div>
        </div>
        {body}
        <div className="modal-actions">{actions}</div>
      </section>
    </div>
  )
}

interface LegalAcceptanceModalProps {
  onAccept: () => void
}

export function LegalAcceptanceModal({ onAccept }: LegalAcceptanceModalProps): JSX.Element {
  return (
    <SimpleModal
      eyebrow="Legal terms"
      title="Acceptance required"
      body={
        <p className="modal-copy">
          This app is an independent educational organizer. Theme names, references and external
          links belong to their respective owners. No official artwork is bundled. You must accept
          these terms before using album features.
        </p>
      }
      actions={
        <button className="primary-button legal-accept-button" type="button" onClick={onAccept}>
          I accept
        </button>
      }
    />
  )
}

interface HelpModalProps {
  eyebrow: string
  title: string
  body: string
  bullets?: string[]
  closeLabel: string
  onClose: () => void
}

export function HelpModal({
  eyebrow,
  title,
  body,
  bullets,
  closeLabel,
  onClose
}: HelpModalProps): JSX.Element {
  return (
    <SimpleModal
      eyebrow={eyebrow}
      title={title}
      body={
        <>
          <p className="modal-copy">{body}</p>
          {bullets && bullets.length > 0 ? (
            <ul className="help-list">
              {bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          ) : null}
        </>
      }
      actions={
        <button className="primary-button" type="button" onClick={onClose}>
          {closeLabel}
        </button>
      }
      topLayer
    />
  )
}

interface ExternalLinkConfirmModalProps {
  closeLabel: string
  onCancel: () => void
  onConfirm: () => void
}

export function ExternalLinkConfirmModal({
  closeLabel,
  onCancel,
  onConfirm
}: ExternalLinkConfirmModalProps): JSX.Element {
  return (
    <SimpleModal
      eyebrow="External link"
      title="You are being redirected"
      body={
        <p className="modal-copy">
          You are about to open a third-party website in your default browser. Continue?
        </p>
      }
      actions={
        <>
          <button className="ghost-button" type="button" onClick={onCancel}>
            {closeLabel}
          </button>
          <button className="primary-button" type="button" onClick={onConfirm}>
            Open site
          </button>
        </>
      }
      topLayer
      onBackdropClick={onCancel}
    />
  )
}
